import { atom, computed } from 'nanostores';
import { tariffs } from '@/data/tariffs';
import { tariffItemParams, trackEvent } from '@/utils/analytics';
import { orderDraftStorage } from './draftStorage';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';

const Settings = {
  stepNames: ['upload_floor_plan', 'design_and_requests', 'review_and_pay'],
  saveDelay: 400,
};

export class OrderStore {
  $isOpened = atom(false);

  /** @type {import('nanostores').PreinitializedWritableAtom<import('@/data/tariffs').TTariff | null>} */
  $tariff = atom(null);

  $stepIndex = atom(0);

  $draftAvailable = atom(false);

  $draftStatus = atom('');

  $isRestoringDraft = atom(false);

  $isPaymentLocked;

  entryPoint = 'homepage_pricing';

  lastTrackedStep = null;

  draft = null;

  draftTimer = null;

  draftSave = null;

  store;

  steps;

  constructor(store) {
    this.store = store;
    this.steps = [new Step1(store), new Step2(store), new Step3(store)];
    this.$isPaymentLocked = computed(
      [this.step3.$isSubmitting, this.step3.$paymentState],
      (submitting, state) => submitting || ['pending', 'success', 'refunded'].includes(state),
    );
  }

  /** @type {Step1} */
  get step1() {
    return this.getStepById('1');
  }

  /** @type {Step2} */
  get step2() {
    return this.getStepById('2');
  }

  /** @type {Step3} */
  get step3() {
    return this.getStepById('3');
  }

  async init() {
    if (!window.indexedDB) return;
    try {
      this.draft = await orderDraftStorage.get();
      this.$draftAvailable.set(Boolean(this.draft));
      if (this.draft?.step3?.paymentState === 'pending') await this.continueDraft();
    } catch (error) {
      this.$draftStatus.set('error');
    }
  }

  openModal(entryPoint = 'homepage_pricing') {
    if (!this.$tariff.get()) this.setTariff(tariffs.en[0]);
    this.entryPoint = entryPoint;
    this.$isOpened.set(true);
    this.store.scrollStore.lock('order');
    this.$draftAvailable.set(Boolean(this.draft) && !this.$isPaymentLocked.get());
    this.updateAllSteps();
    const stepIndex = this.$stepIndex.get();
    if (stepIndex === 0) this.trackBeginCheckout();
    this.trackStepView(stepIndex);
  }

  closeModal() {
    this.$isOpened.set(false);
    this.store.scrollStore.unlock('order');
    this.lastTrackedStep = null;
    this.step3.onLeave();
  }

  trackBeginCheckout() {
    const tariff = this.$tariff.get();
    const quantity = this.step1.floorsCount || 1;
    trackEvent('begin_checkout', {
      checkout_entry_point: this.entryPoint,
      ...tariffItemParams(tariff, quantity),
      value: tariff?.price ? tariff.price * quantity : undefined,
      currency: 'USD',
    });
  }

  trackStepView(stepIndex) {
    if (stepIndex === this.lastTrackedStep) return;
    this.lastTrackedStep = stepIndex;
    trackEvent('checkout_step_view', {
      checkout_step: stepIndex + 1,
      checkout_step_name: Settings.stepNames[stepIndex],
    });
  }

  setTariff(value) {
    if (this.step3.$isSubmitting.get() || this.step3.$paymentState.get() === 'pending') return;
    const completed = ['success', 'refunded'].includes(this.step3.$paymentState.get());
    if (this.$tariff.get() === value && !completed) return;
    const previousDetails = this.$tariff.get() && value && !completed ? this.step3.getDraftData() : null;
    window.clearTimeout(this.draftTimer);
    this.$tariff.set(value);
    this.$stepIndex.set(0);
    if (!previousDetails) this.resetAllSteps();
    if (value) {
      this.steps.forEach((step) => {
        if (step === this.step1 && previousDetails) return;
        step.onTariffChange?.(value);
      });
    }
    if (previousDetails) {
      this.step3.restoreDraft({
        ...previousDetails, paymentLink: '', paymentStatusUrl: '', paymentState: 'idle',
      });
    }
  }

  async setStepIndex(value) {
    if (this.$isPaymentLocked.get() || this.$isRestoringDraft.get()) return;
    const stepIndex = this.$stepIndex.get();
    if (value > stepIndex) {
      const isValid = await this.steps[stepIndex].onValidate?.();
      if (!isValid) return;
    }
    if (value < 0 || value >= this.steps.length) return;
    this.$stepIndex.set(value);
    this.updateAllSteps();
    this.trackStepView(value);
    this.scheduleDraftSave();
  }

  prevStep() {
    const stepIndex = this.$stepIndex.get();
    if (stepIndex > 0) this.setStepIndex(stepIndex - 1);
  }

  nextStep() {
    this.setStepIndex(this.$stepIndex.get() + 1);
  }

  getStepById(id) {
    return this.steps.find((step) => step.id === id);
  }

  setStepById(id) {
    const stepIndex = this.steps.findIndex((step) => step.id === id);
    if (stepIndex !== -1) this.setStepIndex(stepIndex);
  }

  updateAllSteps() {
    const pageIndex = this.$stepIndex.get();
    this.steps.forEach((step, index) => {
      if (index === pageIndex) step.onEnter?.();
      else step.onLeave?.();
    });
  }

  resetAllSteps() {
    this.steps.forEach((step) => step.onReset?.());
  }

  focusField(field) {
    window.requestAnimationFrame(() => {
      const element = document.querySelector(`[data-order-field="${field}"]`);
      const input = element?.matches('input, select, textarea, button, [tabindex]')
        ? element : element?.querySelector('input, select, textarea, button');
      input?.focus?.();
      element?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
  }

  scheduleDraftSave() {
    if (this.$isRestoringDraft.get()) return;
    if (!window.indexedDB) {
      this.$draftStatus.set('error');
      return;
    }
    window.clearTimeout(this.draftTimer);
    this.$draftStatus.set('saving');
    this.draftTimer = window.setTimeout(() => this.saveDraft(), Settings.saveDelay);
  }

  async saveDraft() {
    const tariff = this.$tariff.get();
    if (!tariff || !window.indexedDB) return;
    const draft = {
      tariffId: tariff.tariff,
      stepIndex: this.$stepIndex.get(),
      step1: this.step1.getDraftData(),
      step2: this.step2.getDraftData(),
      step3: this.step3.getDraftData(),
    };
    try {
      this.draftSave = orderDraftStorage.set(draft);
      await this.draftSave;
      this.draft = { ...draft, savedAt: Date.now() };
      this.$draftStatus.set('saved');
    } catch (error) {
      this.$draftStatus.set('error');
    }
  }

  async continueDraft() {
    if (this.$isRestoringDraft.get() || this.$isPaymentLocked.get()) return;
    this.$isRestoringDraft.set(true);
    try {
      const draft = this.draft || await orderDraftStorage.get();
      if (!draft) {
        this.$draftAvailable.set(false);
        return;
      }
      const tariff = tariffs.en.find((item) => item.tariff === draft.tariffId);
      if (!tariff) return;
      this.$tariff.set(tariff);
      this.resetAllSteps();
      this.steps.forEach((step) => step.onTariffChange?.(tariff));
      await this.step1.restoreDraft(draft.step1);
      await this.step2.restoreDraft(draft.step2);
      this.step3.restoreDraft(draft.step3);
      let stepIndex = Math.max(0, Math.min(draft.stepIndex || 0, this.steps.length - 1));
      if (this.step3.$paymentState.get() === 'pending') stepIndex = 2;
      else if (stepIndex > 0 && !await this.step1.onValidate()) stepIndex = 0;
      else if (stepIndex > 1 && !await this.step2.onValidate()) stepIndex = 1;
      this.$stepIndex.set(stepIndex);
      this.$draftAvailable.set(false);
      this.$draftStatus.set('saved');
      this.updateAllSteps();
    } catch (error) {
      this.$stepIndex.set(0);
      this.$draftStatus.set('error');
    } finally {
      this.$isRestoringDraft.set(false);
    }
  }

  async startOver() {
    if (this.$isRestoringDraft.get() || this.step3.$isSubmitting.get() || this.step3.$paymentState.get() === 'pending') return;
    if (!window.confirm('Start over? Your saved checkout data will be removed.')) return;
    const tariff = this.$tariff.get();
    await this.clearDraft();
    this.$stepIndex.set(0);
    this.resetAllSteps();
    if (tariff) this.steps.forEach((step) => step.onTariffChange?.(tariff));
    this.updateAllSteps();
  }

  async clearDraft() {
    window.clearTimeout(this.draftTimer);
    try {
      await this.draftSave?.catch(() => {});
      if (window.indexedDB) await orderDraftStorage.clear();
    } catch (error) {
      this.$draftStatus.set('error');
      return;
    }
    this.draft = null;
    this.$draftAvailable.set(false);
    this.$draftStatus.set('');
  }
}
